import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import moment from 'moment';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { environment } from 'src/environments/environment';
import { MemberDataRequestPayload } from '../../configs/interfaces/authorization/payload.request';
import { ResponsePayload } from '../../configs/interfaces/authorization/payload.response';
import { MemberDataService } from '../member-data/member-data.service';

@Injectable({
  providedIn: 'root'
})
export class UserTokenService {

  private readonly KYRUUS_TOKEN_NAME = 'kyruusToken';
  private readonly KYRUUS_EXPIRATION_TOKEN_NAME = 'kyruusExpirationToken';
  private readonly KYRUUS_AUTHORIZATION_HEADER = 'user-token'

  constructor(
    private brandService: BrandingConfigurationService,
    private memberDataService: MemberDataService,
    private httpClient: HTTPWrapperContext
  ) { }
  
  public async validateSession(lob: LINE_OF_BUSINESS_ENUM, zipCode: string): Promise<void> {

    if (this.isTokenValid()) {
      return;
    }

    const response = await this.acquireKyruusAPIToken(lob, zipCode);

    if (response.body && response.body.success) {
      const kyruusReturnedToken = response.headers.get(this.KYRUUS_AUTHORIZATION_HEADER)
      this.startSession(kyruusReturnedToken);
    } else {
      console.error("Failed to acquire Kyruus API Token");
      throw new Error("Failed to acquire Kyruus API Token");        
    }

  }

  /**
   * This service's purpose is to gain an authorization token to be used for subsequent requests
   * The pattern is that we store a token which is dated in the future, and we check it to see if we're beyond that time
   * if we're beyond, we fetch a new token
   */
  private async acquireKyruusAPIToken(lob: LINE_OF_BUSINESS_ENUM, zipCode: string): Promise<HttpResponse<ResponsePayload>> {
    const payload: MemberDataRequestPayload = {
      member: await this.memberDataService.getMemberData(lob, zipCode)
    };

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/provider/v1/kyruus/v1/login`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      strategyConfigs: { forceModernUrl: true },
      POST_OR_PUT_PAYLOAD: payload,
      withCredentials: true,
      observe: "response" // this exposes the headers, which is where the token is stored
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<HttpResponse<ResponsePayload>>(requestConfigImplementation);
  }

  private jwtDecoder(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  private getTokenExpirationTime(token: string) {
    try {
      if (!token) {
        return null;
      }
      
      const decodedToken = this.jwtDecoder(token);
      
      // Check if the token has an expiration claim
      if (decodedToken && decodedToken.exp) {
        // exp is in seconds since epoch, convert to milliseconds
        return decodedToken.exp * 1000;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting token expiration time:', error);
      return null;
    }
  }
  
  /** TOKEN STUFF */
  // Localstorage setter
  private setKyruusTokens(kyruusToken: string, datetimeInMiliseconds: number): void {
    localStorage.setItem(this.KYRUUS_TOKEN_NAME, kyruusToken);
    localStorage.setItem(this.KYRUUS_EXPIRATION_TOKEN_NAME, datetimeInMiliseconds.toString());
  }

  // Localstorage getter
  private getExpirationToken(): number {
    const datetimeInMilisecondsString: string = localStorage.getItem(this.KYRUUS_EXPIRATION_TOKEN_NAME);
    if (datetimeInMilisecondsString) {
      const datetimeInMilisecondsNumber: number = parseInt(datetimeInMilisecondsString, 10);
      return datetimeInMilisecondsNumber;
    }
    return null;
  }

  public getKyruusLoginToken(): string {
    return localStorage.getItem(this.KYRUUS_TOKEN_NAME);
  }

  // localstorage delete
  private deleteKyruusTokens(): void {
    localStorage.removeItem(this.KYRUUS_TOKEN_NAME);
    localStorage.removeItem(this.KYRUUS_EXPIRATION_TOKEN_NAME);
  }

  // Token validator
  private isTokenValid(): boolean {

    let isValid = false;

    // Check if we have a valid token stored
    const expirationDatetimeInMiliseconds = this.getExpirationToken();

    if (expirationDatetimeInMiliseconds) {
      const expiryTime = moment(expirationDatetimeInMiliseconds).tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]);;
      const currentTime = moment().tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]);;

      if (currentTime < expiryTime) {
        isValid = true;
      } else {
        isValid = false;
      }
    }

    if (!isValid) {
      this.deleteKyruusTokens();
    }

    return isValid;
  }

  // Token expire-watcher, resets upon successful use of the token
  private startSession(kyruusLoginToken: string): boolean {

    // Get token expiry time
    const datetimeInMilisecondsWhenTokenExpires = this.getTokenExpirationTime(kyruusLoginToken); // time 1970

    // Store this time of timeout
    this.setKyruusTokens(
      kyruusLoginToken,
      datetimeInMilisecondsWhenTokenExpires
    );

    return true;

  }

}
